<template>
    <div >
        <confirm id="groupFormModal" ref="groupFormModal" @confirm="saveGroup()">
            <template slot="title">
                <span>{{ groupFormState === 'creating' ? t('clientFields-group_form_modal-title_create') : t('clientFields-group_form_modal-title_edit') }}</span>
            </template>
            <template slot="body">
                <form @submit.prevent="saveGroup">
                    <div class="field">
                        <label for="group_name">{{ t('clientFields-group_form_modal-name_label') }}</label>
                        <input
                            id="group_name"
                            type="text"
                            v-model="groupForm.name"
                            class="form-control"
                            ref="groupNameInput"
                            :placeholder="t('clientFields-group_form_modal-name_placeholder')"
                        />
                    </div>
                </form>
            </template>
        </confirm>

        <confirm id="confirmDeleteGroup" ref="confirmDeleteGroup" @confirm="deleteGroup()">
            <template slot="title">
                <span>
                    {{ t('clientFields-confirm_delete_group-title') }}
                    <b>"{{ groupToModal.name }}"</b>?
                </span>
            </template>
            <template slot="body">
                <template v-if="groupToModal.field_count > 0">
                    <b class="text-danger">{{ t('clientFields-confirm_delete_group-attention') }}</b> {{ t('clientFields-confirm_delete_group-body_has_fields', [groupToModal.field_count]) }}
                </template>
                <template v-else>
                    {{ t('clientFields-confirm_delete_group-body_no_fields') }}
                </template>
            </template>
            <template slot="confirmButton">
                <button class="btn btn-danger" :disabled="groupToModal.field_count > 0" @click="$refs.confirmDeleteGroup.confirm()">
                    {{ t('clientFields-confirm_delete_group-delete_button') }}
                </button>
            </template>
        </confirm>

        <confirm id="confirmDeleteCustomField" ref="confirmDeleteCustomField" @confirm="deleteCustomField(customFieldToShowInModal.id)">
            <template slot="title">
                <span>
                    {{ t('clientFields-confirm_delete_field-title') }}
                    <b>"{{ customFieldToShowInModal.field_name }}"</b>?
                </span>
            </template>
            <template slot="body">
                <template v-if="segmentsRelations.length === 0 && automationsRelations.length === 0">
                    {{ t('clientFields-confirm_delete_field-body_no_relations') }}
                </template>
                <template v-else>
                    {{ t('clientFields-confirm_delete_field-body_linked') }}
                    <template v-if="automationsRelations.length > 0">
                        {{ t('clientFields-confirm_delete_field-linked_automations') }}
                        <ul>
                            <li v-for="automation in automationsRelations" :key="automation.title">
                                {{ automation.title }}
                            </li>
                        </ul>
                    </template>
                    <template v-if="segmentsRelations.length > 0">
                        {{ t('clientFields-confirm_delete_field-linked_segments') }}
                        <ul>
                            <li v-for="segment in segmentsRelations" :key="segment.name">
                                {{ segment.name }}
                            </li>
                        </ul>
                    </template>
                    {{ t('clientFields-confirm_delete_field-warning_linked') }}
                </template>
            </template>
        </confirm>
        <confirm id="confirmEditCustomField" ref="confirmEditCustomField" @confirm="editCustomFieldName(customFieldToShowInModal.id)">
            <template slot="title">
                <span>
                    {{ t('clientFields-confirm_edit_field-title') }}
                    <b>"{{ customFieldToShowInModal.field_name }}"</b>?
                </span>
            </template>
            <template slot="body">
                {{ t('clientFields-confirm_edit_field-body') }}
            </template>
        </confirm>

        <div class="container-fluid">
            <div class="row">
                <div class="col-md-12 col-sm-12">
                    <div class="box clearfix">
                        <template v-if="state === 'loading'">
                            <loader :inline="true"></loader>
                        </template>

                        <template v-if="state === 'list'">
                           <div class="mb-3 d-flex justify-content-between">
                            <h1 class="mb-4">{{ t('clientFields-title') }}</h1>

                            <button
                                class="add-btn-header btn btn-primary mb-4 mx-2"
                                @click.prevent="addNewGroup"
                                :disabled="is('saving')"
                            >
                                <Icon icon-class="ui-plus" />
                                {{ t('clientFields-add_group_button') }}
                            </button>
                           </div>

                            <draggable
                                v-model="fieldGroups"
                                handle=".group-handle"
                                @end="onGroupDragEnd"
                                class="field-group-container"
                            >
                                <div v-for="group in fieldGroups" :key="group.id || 'null-group'" class="field-group-box">
                                    <div class="group-header">
                                        <span class="group-handle icon mdi mdi-dots-vertical" ></span>
                                        <h4 v-if="editingGroupId !== group.id">{{ group.name }}</h4>

                                        <div class="group-actions ml-auto" v-if="group.id !== null">
                                            <button
                                                @click="startEditingGroup(group)"
                                                :aria-label="t('clientFields-aria-edit_group')"
                                                class="btn-action"
                                            >
                                                <Icon icon-class="ui-edit-05" :tooltip="t('clientFields-tooltips-edit_group')" />
                                            </button>
                                            <button
                                                class="trash btn-action"
                                                @click="openConfirmDeleteGroup(group)"
                                                :aria-label="t('clientFields-aria-delete_group')"
                                            >
                                                <Icon icon-class="ui-trash-01" :tooltip="t('clientFields-tooltips-delete_group')" />
                                            </button>
                                        </div>
                                    </div>

                                    <table class="table fields">
                                        <draggable
                                            v-model="group.fields"
                                            tag="tbody"
                                            :group="{ name: 'fields' }"
                                            handle=".field-handle"
                                            @end="onFieldDragEnd"
                                            :data-group-id="group.id || 'null'"
                                        >
                                            <tr v-for="(field, index) in group.fields" :key="field.id" :data-field-id="field.id" :data-index="index" class="field-row">
                                                <td>
                                                    <div class="field-list-item d-flex align-items-center">
                                                        <span class="field-handle icon mdi mdi-dots-vertical" :tooltip="t('clientFields-tooltips-move_field')"></span>

                                                        <div class="field-icon-placeholder" :title="'icone: ui-' + (field.type || 'text')">
                                                            <Icon :icon-class="iconFor(field.type)" size="13" style="color: var(--secondary);" />
                                                        </div>
                                                        <div class="field-list-body">
                                                            <template v-if="editingFieldId === field.id">
                                                                <label :for="'edit_field_' + field.id" class="sr-only">{{ t('clientFields-aria-field_name') }}</label>
                                                                <input
                                                                    :id="'edit_field_' + field.id"
                                                                    :name="'field_name_' + field.id"
                                                                    type="text"
                                                                    v-model="field.field_name"
                                                                    class="form-control"
                                                                    :ref="'editFieldInput_' + field.id"
                                                                    @keydown.enter.prevent="confirmEditFieldName(field)"
                                                                    @blur="cancelEditingField"
                                                                />
                                                            </template>
                                                            <template v-else>
                                                                <div class="field-name">{{ field.field_name }}</div>
                                                                <div class="field-type"><span v-lang="field.type"></span></div>
                                                            </template>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td class="field-actions">
                                                    <button
                                                        v-if="editingFieldId === field.id"
                                                        @click="confirmEditFieldName(field)"
                                                        :aria-label="t('clientFields-aria-confirm_edit')"
                                                    >
                                                        <Icon icon-class="ui-check" />
                                                    </button>
                                                    <button
                                                        v-else
                                                        @click="startEditingField(field)"
                                                        :aria-label="t('clientFields-aria-edit_field')"
                                                    >
                                                        <Icon icon-class="ui-edit-05" :tooltip="t('clientFields-tooltips-edit_field')" />
                                                    </button>
                                                    <button
                                                        class="trash"
                                                        @click="openConfirmDeleteCustomField(field)"
                                                        :aria-label="t('clientFields-aria-delete_field')"
                                                    >
                                                        <Icon icon-class="ui-trash-01" :tooltip="t('clientFields-tooltips-delete_field')" />
                                                    </button>
                                                </td>
                                            </tr>
                                        </draggable>
                                    </table>
                                    <div v-if="group.fields.length === 0" class="empty-group-message">
                                        {{ t('clientFields-empty_group_message') }}
                                    </div>
                                    <button
                                        class="add-btn"
                                        @click.prevent="addNewField(group.id)"
                                        :disabled="is('saving')"
                                    >
                                        <Icon icon-class="ui-plus" />
                                        {{ t('clientFields-add_field_button') }}
                                    </button>
                                </div>
                            </draggable>
                        </template>

                        <template v-if="state === 'form'">
                            <h1>{{ t('clientFields-field_form-title') }}</h1>
                            <form>
                                <div class="fields">
                                    <div class="field">
                                        <label for="field_name">{{ t('clientFields-field_form-name_label') }}</label>
                                        <input
                                            type="text"
                                            v-model="form.field_name"
                                            class="form-control"
                                            id="field_name"
                                        />
                                    </div>
                                    <div class="field mt-2">
                                        <label for="field_type">{{ t('clientFields-field_form-type_label') }}</label>
                                        <select
                                            v-model="form.type"
                                            class="form-control"
                                            id="field_type"
                                        >
                                            <option value="text" v-lang="'text'"></option>
                                            <option value="date" v-lang="'date'"></option>
                                            <option value="user" v-lang="'user'"></option>
                                            <option value="number" v-lang="'number'"></option>
                                        </select>
                                    </div>
                                    </div>
                            </form>
                            <div class="actions">
                                <a
                                    class="btn"
                                    href="#"
                                    @click.prevent="setState('list')"
                                    >{{ t('clientFields-field_form-back_button') }}</a
                                >
                                <button
                                    class="btn btn-success"
                                    @click.prevent="saveField"
                                    :disabled="is('saving')"
                                >
                                    {{ t('clientFields-field_form-save_button') }}
                                </button>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Estilos para Grupos */
.field-group-box {
    border: 1px solid #E5E7EB;
    border-radius: 8px;
    background: #FCFCFD;
    margin-bottom: 20px;
}
.group-header {
    display: flex;
    align-items: center;
    padding: 12px 16px;
    background: #F9FAFB;
    border-bottom: 1px solid #E5E7EB;
}
.group-header h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
}
.group-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}
.group-actions .btn-action {
    background: transparent;
    border: none;
    color: #6B7280;
    padding: 2px;
}
.group-actions .btn-action.trash {
    color: var(--danger);
}

.empty-group-message {
    padding: 20px;
    text-align: center;
    color: #9CA3AF;
    font-style: italic;
}

/* Estilos para Drag & Drop */
.group-handle,
.field-handle {
    cursor: move;
    color: #9CA3AF;
    margin-right: 12px;
}
.field-handle {
    font-size: 16px;
}
.sortable-ghost {
    opacity: 0.4;
    background: #E0E7FF;
}
.field-row.sortable-ghost {
    display: table-row;
}

/* Estilos da Lista Original */
.table.fields td {
    vertical-align: middle;
}
.actions {
    display: flex;
    justify-content: flex-end;
    gap: 20px;
    margin-top: 20px;
}
.field-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
}
.field-actions button {
    background: transparent;
    border: none;
    color: #1F2A37;
    padding: 4px;
    line-height: 1;
}
.field-actions button.trash {
    color: var(--danger);
}
.add-btn-header{
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 4px;
}
.add-btn {
    display: flex;
    padding: 12px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    align-self: stretch;
    border-radius: 0 0 8px 8px; /* Arredonda só embaixo */
    border: 1px solid #E5E7EB;
    border-top: none; /* Remove borda de cima */
    background: #FCFCFD;
    width: 100%;
}
.add-btn:hover {
    background: #F9FAFB;
}
.field-list-item {
    gap: 12px;
}
.field-icon-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 32px;
}
.field-list-body .field-name {
    font-weight: 600;
}
.field-list-body .field-type {
    font-size: 12px;
    color: #9CA3AF;
}
.table.fields {
    margin-bottom: 0;
}
.table.fields td {
    vertical-align: middle;
    border: none;
}
.table.fields tr:last-child td {
    border-bottom: none;
}
</style>

<script>
import keviApi from "../keviApi";
import Icon from "../components/icon/icon";
import draggable from "vuedraggable";
import debounce from "../helpers/debounce";

export default {
    props: {
        user: Object,
    },
    components: {
        Icon,
        draggable,
    },
    data: function () {
        return {
            state: "loading",
            fieldGroups: [],
            form: {
                id: null,
                field_name: null,
                type: "text",
                clients_fields_groups_id: null
            },
            groupFormState: "idle",
            groupForm: {
                id: null,
                name: ""
            },
            groupToModal: {
                id: null,
                name: "",
                field_count: 0
            },
            customFieldToShowInModal: {
                id: null,
                field_name: null,
            },
            segmentsRelations: [],
            automationsRelations: [],
            editingFieldId: null,
            editingGroupId: null,
            debouncedSavePresentation: null,
        };
    },
    computed: {
        place() {
            return this.$store.state.place;
        },
    },
    async mounted() {
        document.getElementsByTagName("title")[0].innerText =
            this.t("clientFields-page_title");

        this.debouncedSavePresentation = debounce(this.savePresentationBatch, 1000);

        await this.loadFields();
        this.setState("list");
    },
    methods: {
        t: function (term, replaces) {
            return this.$root.t(term, replaces);
        },
        iconFor(type) {
            switch (type) {
                case "date": return "ui-calendar";
                case "user": return "ui-user-square";
                case "number": return "ui-number";
                case "textarea": return "ui-type-square";
                case "checkbox": return "ui-check-square-broken";
                case "coupon": return "ui-ticket-01";
                default: return "ui-type-square";
            }
        },
        async loadFields() {
            let options = keviApi.getOptions("/grouped/client-fields", "GET");
            const response = await this.$http(options);
            this.fieldGroups = JSON.parse(JSON.stringify(response.body));
        },
        is: function (state) {
            return this.state === state;
        },
        setState: function (state) {
            this.state = state;
        },

        addNewField(groupId = null) {
            this.setState("form");
            this.form = {
                id: null,
                field_name: "",
                type: "text",
                clients_fields_groups_id: groupId
            };
        },
        saveField() {
            const options = keviApi.getOptions("client-fields", "PUT", {
                name: this.form.field_name,
                type: this.form.type,
                clients_fields_groups_id: this.form.clients_fields_groups_id
            });
            window?.mixpanel?.track("Settings - save client field");
            this.$http(options).then(async () => {
                this.$store.dispatch("notification/success", this.t("clientFields-notifications-field_added"));
                this.setState("loading");
                await this.loadFields();
                this.setState("list");
            });
        },
        openConfirmDeleteCustomField(customField) {
            const options = keviApi.getOptions("client-fields/" + customField.id + "/relations", "GET");
            this.$http(options).then((response) => {
                this.segmentsRelations = response.body.segments;
                this.automationsRelations = response.body.automations;
            });
            this.customFieldToShowInModal = customField;
            this.$refs["confirmDeleteCustomField"].open();
        },
        deleteCustomField(id) {
            const options = keviApi.getOptions("client-fields/" + id, "DELETE");
            window?.mixpanel?.track("Settings - delete client field");
            this.$http(options).then(() => {
                for (const group of this.fieldGroups) {
                    const fieldIndex = group.fields.findIndex(f => f.id === id);
                    if (fieldIndex !== -1) {
                        group.fields.splice(fieldIndex, 1);
                        break;
                    }
                }
                this.$store.dispatch("notification/success", this.t("clientFields-notifications-field_deleted"));
            });
        },
        startEditingField(field) {
            this.editingFieldId = field.id;
            this.$nextTick(() => {
                const refName = "editFieldInput_" + field.id;
                const refEl = this.$refs[refName];
                const el = Array.isArray(refEl) ? refEl[0] : refEl;
                if (el && typeof el.focus === "function") {
                    el.focus();
                }
            });
        },
        cancelEditingField() {
            this.editingFieldId = null;
        },
        confirmEditFieldName(field) {
            this.customFieldToShowInModal = { ...field };
            this.$refs["confirmEditCustomField"].open();
        },
        editCustomFieldName(id) {
            const options = keviApi.getOptions("client-fields/" + id, "PUT", {
                field_name: this.customFieldToShowInModal.field_name,
            });
            window?.mixpanel?.track("Settings - edit client field name");
            this.$http(options).then(() => {
                for (const group of this.fieldGroups) {
                    const field = group.fields.find(f => f.id === id);
                    if (field) {
                        field.field_name = this.customFieldToShowInModal.field_name;
                        break;
                    }
                }
                this.$store.dispatch("notification/success", this.t("clientFields-notifications-field_edited"));
                this.editingFieldId = null;
            });
        },

        addNewGroup() {
            this.groupFormState = 'creating';
            this.groupForm = { id: null, name: "" };
            this.$refs.groupFormModal.open();
            this.$nextTick(() => this.$refs.groupNameInput.focus());
        },

        startEditingGroup(group) {
            this.groupFormState = 'editing';
            this.groupForm = { id: group.id, name: group.name };
            this.$refs.groupFormModal.open();
            this.$nextTick(() => this.$refs.groupNameInput.focus());
        },

        saveGroup() {
            if (!this.groupForm.name) {
                this.$store.dispatch("notification/error", this.t("clientFields-notifications-group_name_required"));
                return;
            }

            if (this.groupFormState === 'creating') {
                const options = keviApi.getOptions("client-field-groups", "PUT", {
                    name: this.groupForm.name,
                    display_order: this.fieldGroups.length,
                });
                this.$http(options).then(async () => {
                    this.$store.dispatch("notification/success", this.t("clientFields-notifications-group_created"));
                    this.$refs.groupFormModal.close();
                    await this.loadFields();
                }).catch(err => {
                    console.error("Falha ao criar grupo", err);
                    this.$store.dispatch("notification/error", this.t("clientFields-notifications-group_create_error"));
                });

            } else if (this.groupFormState === 'editing') {
                const options = keviApi.getOptions(`client-field-groups/${this.groupForm.id}`, "PUT", {
                    name: this.groupForm.name,
                });
                this.$http(options).then(() => {
                    const group = this.fieldGroups.find(g => g.id === this.groupForm.id);
                    if (group) {
                        group.name = this.groupForm.name;
                    }
                    this.$store.dispatch("notification/success", this.t("clientFields-notifications-group_updated"));
                    this.$refs.groupFormModal.close();
                }).catch(err => {
                    console.error("Falha ao atualizar grupo", err);
                    this.$store.dispatch("notification/error", this.t("clientFields-notifications-group_update_error"));
                });
            }
        },

        openConfirmDeleteGroup(group) {
            this.groupToModal = {
                id: group.id,
                name: group.name,
                field_count: group.fields.length,
            };
            this.$refs.confirmDeleteGroup.open();
        },

        deleteGroup() {
            const groupId = this.groupToModal.id;
            if (this.groupToModal.field_count > 0) {
                this.$store.dispatch("notification/error", this.t("clientFields-notifications-group_has_fields_error"));
                return;
            }

            const options = keviApi.getOptions(`client-field-groups/${groupId}`, "DELETE");
            this.$http(options).then(async () => {
                this.$store.dispatch("notification/success", this.t("clientFields-notifications-group_deleted"));
                this.$refs.confirmDeleteGroup.close();
                const groupIndex = this.fieldGroups.findIndex(g => g.id === groupId);
                if (groupIndex !== -1) {
                    this.fieldGroups.splice(groupIndex, 1);
                }
                this.savePresentationBatch();
            }).catch(err => {
                console.error("Falha ao excluir grupo", err);
                const errorMsg = err.body.error || this.t("clientFields-notifications-group_delete_error");
                this.$store.dispatch("notification/error", errorMsg);
            });
        },

        onFieldDragEnd(event) {
            this.debouncedSavePresentation();
        },

        onGroupDragEnd(event) {
            this.debouncedSavePresentation();
        },

        savePresentationBatch() {
            const payload = {
                groups: this.fieldGroups.map(group => ({
                    group_id: group.id,
                    fields: group.fields.map(field => field.id)
                }))
            };

            const options = keviApi.getOptions(
                `client-fields/presentation-batch`,
                "POST",
                payload
            );

            this.$http(options).then(() => {
                 this.$store.dispatch(
                    "notification/success",
                    this.t("clientFields-notifications-order_saved"),
                );
            }).catch(err => {
                console.error("Falha ao salvar ordem em lote", err);
                this.$store.dispatch("notification/error", this.t("clientFields-notifications-order_save_error"));
                this.loadFields();
            });
        },
    },
};
</script>
